// Linh Thực, Luyện Đan, đan độc, công trình động phủ (CRAFTING §1–2, CULTIVATION §13, DONG_PHU §3).
// Hàm thuần: niên phân tính từ mốc, lò giải bằng RNG riêng của mẻ.
import { BALANCE } from '@data/balance';
import {
  BUILDINGS,
  FURNACE_LV_THRESHOLDS,
  GARDEN_BASE_PLOTS,
  buildingDef,
  herbDef,
  pillDef,
  recipeDef,
  type BuildingId,
  type HeatStep,
  type PillGrade,
  type RecipeDef,
} from '@data/crafting';
import {
  goodsDef,
  parseStackKey,
  stackKey,
  HERB_AGES,
  type HerbAge,
  type StackGrade,
} from '@data/items/goods';
import { createRng, type Rng } from '../core/rng';
import { HOUR_MS } from '../core/time';

const MIN_MS = 60_000;

// ── Công trình
export interface BuildingState {
  lv: number;
  upgrade: { toLv: number; endMs: number } | null;
}
export type Buildings = Record<BuildingId, BuildingState>;

export function defaultBuildings(): Buildings {
  return Object.fromEntries(BUILDINGS.map((b) => [b.id, { lv: 1, upgrade: null }])) as Buildings;
}

export function upgradeCost(
  id: BuildingId,
  toLv: number,
): { stones: number; material: { id: string; count: number }; minutes: number } {
  const d = buildingDef(id);
  return {
    stones: Math.round(d.baseStone * Math.pow(BALANCE.BUILDING_COST_GROWTH, toLv - 1)),
    material: { id: d.materialPerLv.id, count: d.materialPerLv.perLv * toLv },
    minutes: BALANCE.BUILD_MIN_BASE * Math.pow(BALANCE.BUILD_MIN_GROWTH, toLv - 1),
  };
}

export function buildingCap(id: BuildingId, realmIdx: number): number {
  return buildingDef(id).capByRealm[realmIdx] ?? 1;
}

/** Hoàn tất nâng cấp đã tới hạn (closed-form, chạy cả khi offline). */
export function settleBuildings(
  b: Buildings,
  nowMs: number,
): { buildings: Buildings; finished: BuildingId[] } {
  const out = { ...b };
  const finished: BuildingId[] = [];
  for (const id of Object.keys(out) as BuildingId[]) {
    const s = out[id];
    if (s.upgrade && s.upgrade.endMs <= nowMs) {
      out[id] = { lv: s.upgrade.toLv, upgrade: null };
      finished.push(id);
    }
  }
  return { buildings: out, finished };
}

export function gardenPlots(b: Buildings): number {
  return GARDEN_BASE_PLOTS + (b.herbGarden.lv - 1);
}

export function furnaceCount(b: Buildings): number {
  return FURNACE_LV_THRESHOLDS.filter((t) => b.pillRoom.lv >= t).length;
}

// ── Linh Thực
export interface PlotState {
  herbId: string | null;
  plantedAtMs: number;
  bonusYears: number;
}

export function growthMult(b: Buildings): number {
  return 1 + BALANCE.HERB_GROWTH_PER_LV * (b.herbGarden.lv - 1);
}

/** Niên phân của ô ở `nowMs`: < HERB_BASE_YEARS nghĩa là chưa thành thục (tỉ lệ tới thập niên). */
export function plotYears(p: PlotState, nowMs: number, g: number): number {
  if (!p.herbId) return 0;
  const h = Math.max(0, nowMs - p.plantedAtMs) / HOUR_MS;
  const mature = herbDef(p.herbId).matureH / g;
  if (h < mature) return (BALANCE.HERB_BASE_YEARS * h) / mature;
  return BALANCE.HERB_BASE_YEARS + (h - mature) * BALANCE.HERB_YEARS_PER_H * g + p.bonusYears;
}

export function plotMatureAtMs(p: PlotState, g: number): number {
  return p.herbId ? p.plantedAtMs + (herbDef(p.herbId).matureH / g) * HOUR_MS : Infinity;
}

export function isMature(p: PlotState, nowMs: number, g: number): boolean {
  return !!p.herbId && nowMs >= plotMatureAtMs(p, g);
}

/** Thu hoạch: yield × (1 + Dược Viên Sản Lượng %) (phần lẻ thành cơ hội +1), 70% hoàn 1 hạt. */
export function harvestPlot(
  p: PlotState,
  nowMs: number,
  g: number,
  herbYieldPct: number,
  rng: Rng,
): { herbId: string; years: number; count: number; seedBack: boolean } | null {
  if (!p.herbId || !isMature(p, nowMs, g)) return null;
  const d = herbDef(p.herbId);
  const exp = d.yield * (1 + Math.max(0, herbYieldPct) / 100);
  const count = Math.floor(exp) + (rng.next() < exp - Math.floor(exp) ? 1 : 0);
  return {
    herbId: d.id,
    years: plotYears(p, nowMs, g),
    count,
    seedBack: rng.next() < BALANCE.HERB_SEED_RETURN,
  };
}

// ── Luyện Đan
export interface AlchemyState {
  lv: number;
  xp: number;
  knownRecipes: string[];
}

export interface CraftJob {
  id: string;
  recipeId: string;
  startMs: number;
  endMs: number;
  rngSeed: number;
  /** herbQ đã chốt lúc bắt đầu (nguyên liệu bị trừ khi mẻ bắt đầu). */
  herbQ: number;
  /** Hỏa hầu đã chọn. */
  pattern: [HeatStep, HeatStep, HeatStep];
  /**
   * Đúng các chồng đã trừ khi bấm (khóa chồng → số, vd `herb.dewGrass@century: 2`), để hủy mẻ hoàn
   * đúng niên phân. Mẻ lưu trước khi có trường này: hoàn theo id gốc của đan phương (DECISIONS M2).
   */
  used?: Record<string, number>;
}

export function alchemyLvCap(realmIdx: number): number {
  return BALANCE.ALCHEMY_LV_CAP_PER_REALM * (realmIdx + 1);
}

export function alchemyXpNeed(lv: number): number {
  return BALANCE.ALCHEMY_XP_BASE * Math.pow(BALANCE.ALCHEMY_XP_GROWTH, lv - 1);
}

const AGE_Q = (a: HerbAge): number => BALANCE.HERB_AGE_Q[a];

/**
 * herbQ = trung bình có trọng số HERB_AGE_Q của nguyên liệu thảo dược (dược chính trọng số 2). Một loại
 * dược trộn nhiều niên phân (mảng, mỗi phần tử một cây) thì lấy trung bình HERB_AGE_Q của các cây.
 */
export function herbQuality(
  recipe: RecipeDef,
  picked: Readonly<Record<string, HerbAge | readonly HerbAge[]>>,
): number {
  let sum = 0;
  let w = 0;
  for (const ing of recipe.ingredients) {
    if (goodsDef(ing.id).kind !== 'herb') continue;
    const k = ing.key ? 2 : 1;
    const p = picked[ing.id] ?? 'decade';
    const ages: readonly HerbAge[] = typeof p === 'string' ? [p] : p.length ? p : ['decade'];
    sum += (ages.reduce((a, x) => a + AGE_Q(x), 0) / ages.length) * k;
    w += k;
  }
  return w > 0 ? sum / w : 0;
}

/**
 * Chọn nguyên liệu từ kho cho một mẻ: dược lấy niên phân cao trước, thiếu thì bù bằng niên phân thấp
 * hơn (trộn được, không dưới `minAge` nếu đan phương đòi); nguyên liệu khác lấy đúng id. Thiếu thì null.
 */
export function pickIngredientsFrom(
  recipe: RecipeDef,
  goods: Readonly<Record<string, number>>,
): { keys: Record<string, number>; ages: Record<string, HerbAge[]> } | null {
  const keys: Record<string, number> = {};
  const ages: Record<string, HerbAge[]> = {};
  for (const ing of recipe.ingredients) {
    if (goodsDef(ing.id).kind === 'herb') {
      let need = ing.count;
      const took: HerbAge[] = [];
      const minIdx = HERB_AGES.indexOf(ing.minAge ?? 'decade');
      for (const a of [...HERB_AGES].reverse()) {
        if (need <= 0 || HERB_AGES.indexOf(a) < minIdx) break;
        const k = stackKey(ing.id, { age: a });
        const n = Math.min(need, (goods[k] ?? 0) - (keys[k] ?? 0));
        if (n <= 0) continue;
        keys[k] = (keys[k] ?? 0) + n;
        for (let i = 0; i < n; i++) took.push(a);
        need -= n;
      }
      if (need > 0) return null;
      ages[ing.id] = [...(ages[ing.id] ?? []), ...took];
    } else {
      if ((goods[ing.id] ?? 0) - (keys[ing.id] ?? 0) < ing.count) return null;
      keys[ing.id] = (keys[ing.id] ?? 0) + ing.count;
    }
  }
  return { keys, ages };
}

/**
 * Giờ bắt đầu của mẻ mới: xếp lại các mẻ hiện có lên `furnaces` lò theo giờ bắt đầu (mỗi mẻ vào lò rảnh
 * sớm nhất), mẻ mới vào lò rảnh sớm nhất, không sớm hơn `nowMs`.
 */
export function nextJobStart(jobs: readonly CraftJob[], furnaces: number, nowMs: number): number {
  const free = Array.from({ length: Math.max(1, furnaces) }, () => nowMs);
  for (const j of [...jobs].sort((a, b) => a.startMs - b.startMs)) {
    let i = 0;
    for (let k = 1; k < free.length; k++) if (free[k]! < free[i]!) i = k;
    free[i] = Math.max(free[i]!, j.endMs);
  }
  return Math.max(nowMs, Math.min(...free));
}

export function heatScore(recipe: RecipeDef, pattern: readonly HeatStep[]): number {
  let s = 0;
  for (let i = 0; i < 3; i++)
    s += recipe.pattern[i] === pattern[i] ? BALANCE.HEAT_STEP_OK : BALANCE.HEAT_STEP_BAD;
  return s;
}

/** Nhiễu Normal(0, SD) cắt ±CLAMP (Box–Muller, 2 lần rút). */
function noise(rng: Rng): number {
  const u = Math.max(1e-12, rng.next());
  const v = rng.next();
  const z = Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
  const c = BALANCE.ALCHEMY_NOISE_CLAMP;
  return Math.max(-c, Math.min(c, z * BALANCE.ALCHEMY_NOISE_SD));
}

export interface CraftCtx {
  alchemyLv: number;
  pillRoomLv: number;
  fire: 'mortal' | 'earth';
}

/** Q trước nhiễu (CRAFTING §2.4, P0: không Khống Hỏa, không cơ duyên). */
export function qualityBase(
  recipe: RecipeDef,
  ctx: CraftCtx,
  herbQ: number,
  pattern: readonly HeatStep[],
): number {
  return (
    BALANCE.ALCHEMY_Q_BASE +
    Math.min(BALANCE.ALCHEMY_Q_LV_CAP, BALANCE.ALCHEMY_Q_PER_LV * Math.max(0, ctx.alchemyLv - recipe.minLv)) +
    BALANCE.FIRE_Q[ctx.fire] +
    herbQ +
    heatScore(recipe, pattern) +
    BALANCE.ALCHEMY_ROOM_Q_PER_LV * ctx.pillRoomLv
  );
}

export function gradeOf(q: number): PillGrade {
  const g = BALANCE.PILL_GRADE_Q;
  return q >= g.peak ? 'peak' : q >= g.high ? 'high' : q >= g.mid ? 'mid' : q >= g.low ? 'low' : 'waste';
}

export interface CraftResult {
  q: number;
  grade: PillGrade;
  /** Khóa chồng + số lượng đầu ra (phế đan → Đan Tra). */
  output: { stack: string; count: number };
  xp: number;
}

export function resolveCraft(job: CraftJob, ctx: CraftCtx): CraftResult {
  const r = recipeDef(job.recipeId);
  const rng = createRng(job.rngSeed);
  const q = qualityBase(r, ctx, job.herbQ, job.pattern) + noise(rng);
  const grade = gradeOf(q);
  const output =
    grade === 'waste'
      ? { stack: 'mat.pillDregs', count: 1 }
      : { stack: `${r.outputId}#${grade}`, count: r.yield };
  return { q, grade, output, xp: BALANCE.ALCHEMY_XP_PER_TIER * r.tier * BALANCE.ALCHEMY_GRADE_XP[grade] };
}

export function jobDurationMs(recipeId: string): number {
  return recipeDef(recipeId).craftMin * MIN_MS;
}

/** Cộng kinh nghiệm Đan Thuật, lên cấp tới trần cảnh giới. */
export function addAlchemyXp(a: AlchemyState, xp: number, realmIdx: number): AlchemyState {
  let { lv, xp: cur } = a;
  cur += xp;
  const cap = alchemyLvCap(realmIdx);
  while (lv < cap && cur >= alchemyXpNeed(lv)) {
    cur -= alchemyXpNeed(lv);
    lv++;
  }
  return { ...a, lv, xp: cur };
}

// ── Đan độc (CULTIVATION §13)
export function toxicityEfficacy(tox: number): number {
  const safe = BALANCE.PILL_TOXICITY_SAFE;
  if (tox < safe) return 1;
  return Math.max(
    BALANCE.PILL_TOXICITY_MIN_EFFICACY,
    1 - ((1 - BALANCE.PILL_TOXICITY_MIN_EFFICACY) * (tox - safe)) / (BALANCE.PILL_TOXICITY_MAX - safe),
  );
}

export type PillBlock = 'toxicity' | 'battleOnly' | 'breakthroughOnly' | 'unknown';

/** Uống được không: ≥ 100 đan độc thì chặn (trừ Thanh Độc Đan, P1); đan trận / phá cảnh dùng chỗ khác. */
export function pillBlock(stackKeyStr: string, tox: number): PillBlock | null {
  const { id } = parseStackKey(stackKeyStr);
  const d = pillDef(id);
  if (!d) return 'unknown';
  if (d.effect.kind === 'battleHeal') return 'battleOnly';
  if (d.effect.kind === 'breakthrough') return 'breakthroughOnly';
  if (tox >= BALANCE.PILL_TOXICITY_MAX) return 'toxicity';
  return null;
}

export interface PillUse {
  efficacy: number;
  toxAfter: number;
  buff?: { id: string; bucket: number; untilMs: number };
}

/**
 * Uống một viên: hiệu lực theo đan độc TRƯỚC khi uống, rồi cộng độc (trần 100).
 * `toxResPct`: Kháng Đan Độc % từ trang bị (Hương Nang), giảm phần độc viên này thêm vào.
 */
export function usePill(stackKeyStr: string, tox: number, nowMs: number, toxResPct = 0): PillUse {
  const { id, grade } = parseStackKey(stackKeyStr);
  const d = pillDef(id);
  const efficacy = toxicityEfficacy(tox);
  const res = Math.min(100, Math.max(0, toxResPct)) / 100;
  const toxAfter = Math.min(BALANCE.PILL_TOXICITY_MAX, tox + (d?.toxicity ?? 0) * (1 - res));
  if (d?.effect.kind === 'qiBuff') {
    const bucket = (d.gradeBucket?.[grade as StackGrade] ?? d.effect.bucket) * efficacy;
    return { efficacy, toxAfter, buff: { id, bucket, untilMs: nowMs + d.effect.hours * HOUR_MS } };
  }
  return { efficacy, toxAfter };
}

/** Luyện hóa đan độc: 10/giờ bế quan, 3/giờ Lịch Luyện. */
export function decayToxicity(tox: number, hours: number, trial: boolean): number {
  const rate = trial ? BALANCE.PILL_TOXICITY_DECAY_TRIAL_PER_H : BALANCE.PILL_TOXICITY_DECAY_PER_H;
  return Math.max(0, tox - rate * hours);
}

export { createRng };
